import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { format } from "date-fns";
import { Typography } from "@platform/design-system";

import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactElement } from "react";

/**
 * Which quote a version column holds, as far as the analyst is concerned (ADR-031).
 *
 * `deleted` outranks `archived`: deleting is permitted only on the active version, so a
 * deleted group can also read as actioned-and-inactive, and the deletion is the fact worth
 * showing.
 */
export type QuoteVersionState = "active" | "pending" | "archived" | "deleted";

/** Suffix appended to the version number, e.g. "V2 - Archived". Active shows none. */
const STATE_LABEL: Record<Exclude<QuoteVersionState, "active">, string> = {
  pending: "Pending",
  archived: "Archived",
  deleted: "Deleted",
};

/**
 * Shared styling for the version-tier chips (V1/V2/… and "Current"), from
 * Figma: a filled `action.selected` background with the theme's 4px radius
 * (`borderRadius: 1` = 1 × theme.shape.borderRadius) rather than the default
 * pill-shaped Chip.
 */
export const versionChipSx: SxProps<Theme> = {
  borderRadius: 1,
  backgroundColor: "action.selected",
};

/**
 * Per-state chip fills. `secondary.main` is Figma's purple: this app's `primary`/`secondary`
 * are inverted from `kaizenTheme` (CD-2036), so the purple lives on `secondary`, not
 * `primary`. The chip fill itself has no theme match — closest is `purple.100` at `#f3ebf3`,
 * a different value — so it stays a literal.
 *
 * Each chip carries its own text colour because it sits on a tinted header — the default
 * inherits and drops out of contrast.
 */
const HISTORICAL_CHIP_SX: SxProps<Theme> = {
  ...versionChipSx,
  // grey.200 against the near-white header fill below, so the chip stays a chip. Matching
  // the two is what made it disappear into its own background.
  backgroundColor: "grey.200",
  color: "text.primary",
};

const STATE_CHIP_SX: Record<QuoteVersionState, SxProps<Theme>> = {
  active: versionChipSx,
  pending: {
    ...versionChipSx,
    backgroundColor: "#f3eaf2",
    color: "brandPrimary.main",
  },
  archived: HISTORICAL_CHIP_SX,
  deleted: HISTORICAL_CHIP_SX,
};

export interface RequoteBadgeProps {
  /**
   * The carrier's Nth quote for this line of coverage, rendered as the V1/V2/… chip.
   * Coverage-scoped, not the RFP-wide version (CD-1767).
   */
  quoteVersion: number;
  /** ISO timestamp the quote was received, rendered next to the version chip. */
  receivedAt?: string;
  /** Defaults to `active`, the column that is currently the carrier's quote. */
  state?: QuoteVersionState;
}

/**
 * RequoteBadge renders a version chip (V1/V2/…) plus the received timestamp for
 * a real proposal column in the SBS grid's Version tier (ADR-031).
 *
 * The chip names the column's state outright rather than leaving it to colour alone, so a
 * reader does not have to know that grey means archived. The timestamp goes italic for the
 * two historical states, which is the one cue that survives when several grey columns sit
 * side by side.
 */
export const RequoteBadge = ({
  quoteVersion,
  receivedAt,
  state = "active",
}: RequoteBadgeProps): ReactElement => {
  const isHistorical = state === "archived" || state === "deleted";

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Chip
        label={
          state === "active"
            ? `V${quoteVersion}`
            : `V${quoteVersion} - ${STATE_LABEL[state]}`
        }
        size="small"
        sx={STATE_CHIP_SX[state]}
      />
      {receivedAt && (
        <Typography variant="caption" color="textSecondary">
          {isHistorical ? (
            <em>{format(new Date(receivedAt), "MM/dd/yyyy h:mm a")}</em>
          ) : (
            format(new Date(receivedAt), "MM/dd/yyyy h:mm a")
          )}
        </Typography>
      )}
    </Stack>
  );
};

RequoteBadge.displayName = "RequoteBadge";
